import type { CustomerNewOrderTask as NewOrderTask } from '@einwert/valuation-api-client'
import { createFilterOptions, GridLegacy as Grid, TextField } from '@mui/material'
import {
    assembleNameWithEmailFallback,
    assembleUserSearchText,
    FormAutocomplete,
    FormDatePicker,
    FormTemplate,
    FormTextField,
    LoadingSkeleton,
    Modal,
    Select,
    tokens,
    UserBubbleWithName,
} from 'ComponentLibrary'
import { isEmpty, noop } from 'lodash'
import { useState } from 'react'
import { useCreateNewTaskData } from './CreateNewTask.data'
import type { RootUser as User } from '@einwert/valuation-api-client'
import { useForm, useWatch } from 'react-hook-form'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type CreateNewTaskProps = {
    isOpen: boolean
    handleClose: () => void
    orderTitle: string
}

export function CreateNewTask({ isOpen, handleClose, orderTitle }: CreateNewTaskProps) {
    const { t } = useTranslation('pages', { keyPrefix: 'orders.taskListPage' })
    const { t: tTerminology } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tCommonConfirmation } = useTranslation('terminology', { keyPrefix: 'common.confirmation' })
    const locale = useCurrentResolvedLocale()
    const [createMore, setCreateMore] = useState(false)

    const defaultValues = { title: '', status: 'open' as const, description: '', assignee: undefined, due_date: undefined }

    const {
        control,
        formState: { errors, dirtyFields, isDirty, isValid },
        handleSubmit,
        reset,
    } = useForm<NewOrderTask>({
        defaultValues,
    })

    const resetForm = () => {
        reset(defaultValues)
    }

    const closeDialog = () => {
        resetForm()
        handleClose()
    }

    const { createNewTask, isCreatingTask, isUsersPending, users } = useCreateNewTaskData({
        onSuccess: createMore ? resetForm : closeDialog,
    })

    const onSubmit = handleSubmit(async (formData) => {
        await createNewTask(formData)
    })

    const assignee = useWatch({ control, name: 'assignee' })

    return (
        <Modal
            title={t('create.title')}
            showLoading={isCreatingTask}
            isOpen={isOpen}
            onClose={closeDialog}
            showCloseIcon
            closeIconTooltip={tCommonActions('close')}
            footerProps={{
                primaryButton: {
                    text: t('create.create'),
                    onClick: onSubmit,
                    disabled: !isValid || !isDirty || isCreatingTask,
                },
                secondaryButton: {
                    text: t('create.cancel'),
                    onClick: closeDialog,
                },
            }}
        >
            <FormTemplate
                isFormDirty={!!dirtyFields}
                onSubmit={onSubmit}
                onCancel={closeDialog}
                isPersisting={isCreatingTask}
                shouldDisableSubmit={isEmpty(dirtyFields)}
                confirmationDialogTitle={tCommonConfirmation('discardAllUnsavedChanges')}
                confirmationDialogConfirmLabel={tCommonConfirmation('discard')}
                confirmationDialogCancelLabel={tCommonConfirmation('cancel')}
                hideFooter
            >
                <Grid item xs={12} marginTop={tokens.SizingSpacing100}>
                    <Select
                        inputLabel={tTerminology('order')}
                        options={[{ label: t('create.orderOption', { orderTitle }), value: orderTitle }]}
                        value={orderTitle}
                        onChange={noop} // this can not be changed
                        fullWidth
                        disabled
                    />
                </Grid>
                <FormTextField control={control} errors={errors} fieldName="title" label={t('create.taskTitle')} xs={12} required />
                <Grid item xs={12}>
                    <FormDatePicker control={control} errors={errors} name="due_date" label={t('create.dueDate')} locale={locale} />
                </Grid>
                <Grid item xs={12}>
                    {isUsersPending ? (
                        <LoadingSkeleton height="55px" width="100%" sx={{ transform: 'none' }} />
                    ) : (
                        <FormAutocomplete
                            key={assignee?.id}
                            autocompleteProps={{
                                renderInput: (params) => <TextField {...params} label={t('create.assignee')} />,
                                options: users,
                                groupBy: (option) => option.organization_name,
                                isOptionEqualToValue: (option, value) => option.id === value.id,
                                fullWidth: true,
                                getOptionLabel: (option) => assembleNameWithEmailFallback(option),
                                filterOptions: (options, state) =>
                                    userFilterOptions(
                                        options.filter((user) => user.is_active !== false),
                                        state
                                    ),
                                renderOption: (listAttributes, user) => (
                                    <li {...listAttributes}>
                                        <UserBubbleWithName user={user} size={tokens.SizingIconM} />
                                    </li>
                                ),
                            }}
                            control={control}
                            name="assignee"
                        />
                    )}
                </Grid>
                <FormTextField
                    xs={12}
                    control={control}
                    errors={errors}
                    label={t('create.description')}
                    multiline
                    fieldName="description"
                />
                <Grid item xs={12}>
                    <Checkbox
                        ellipsis
                        checked={createMore}
                        label={t('create.createMore')}
                        onChange={(_, checked) => setCreateMore(checked)}
                    />
                </Grid>
            </FormTemplate>
        </Modal>
    )
}

const userFilterOptions = createFilterOptions<User>({
    stringify: assembleUserSearchText,
})
